import { Box, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React, { useEffect, useState } from "react";
import Countdown, { CountdownRenderProps } from "react-countdown";

const TimeUnit = ({ value, label, isLight }: { value: number; label: string; isLight: boolean }) => (
  <Stack
    data-testid={`live-countdown-${label.toLowerCase()}-card`}
    alignItems="center"
    justifyContent="center"
    sx={{
      backgroundColor: isLight ? "common.white" : "primary.main",
      borderRadius: "2rem",
      width: { xs: "7.7rem", sm: "11.4rem" },
      boxSizing: "border-box",
      padding: "2rem 1.6rem",
      gap: "0.4rem",
      boxShadow: 3
    }}
  >
    <Typography
      component="span"
      data-testid={`live-countdown-${label.toLowerCase()}-value`}
      sx={{
        width: "100%",
        textAlign: "center",
        color: isLight ? "primary.main" : "common.white",
        fontSize: { xs: "2.6rem", md: "2.8rem", lg: "3.2rem" },
        fontWeight: 800,
        lineHeight: { xs: "3.2rem", md: "3.6rem", lg: "4rem" }
      }}
    >
      {String(value).padStart(2, "0")}
    </Typography>
    <Typography
      component="span"
      data-testid={`live-countdown-${label.toLowerCase()}-label`}
      sx={{
        color: isLight ? "secondary.main" : "warning.main",
        fontSize: { xs: "1.2rem", md: "1.4rem" },
        fontWeight: 700,
        lineHeight: { xs: "1.8rem", md: "2rem" },
        letterSpacing: 0,
        textTransform: "uppercase"
      }}
    >
      <Box
        component="span"
        data-testid={`live-countdown-${label.toLowerCase()}-short-label`}
        aria-hidden="true"
        sx={{ display: { xs: "inline", sm: "none" } }}
      >
        {label.charAt(0)}
      </Box>
      <Box
        component="span"
        data-testid={`live-countdown-${label.toLowerCase()}-full-label`}
        sx={(theme) => ({
          ...visuallyHidden,
          [theme.breakpoints.up("sm")]: {
            position: "static",
            width: "auto",
            height: "auto",
            margin: 0,
            overflow: "visible",
            clip: "auto",
            whiteSpace: "normal"
          }
        })}
      >
        {label}
      </Box>
    </Typography>
  </Stack>
);

const TimeSeparator = ({ unit }: { unit: string }) => (
  <Typography
    component="span"
    data-testid={`live-countdown-${unit}-separator`}
    aria-hidden="true"
    sx={{
      display: { xs: "none", sm: "block" },
      color: "common.white",
      fontSize: "3.2rem",
      fontWeight: 700,
      lineHeight: 1
    }}
  >
    :
  </Typography>
);

const renderTimer = (
  { days, hours, minutes, seconds, completed }: CountdownRenderProps,
  isLight: boolean
) => {
  if (completed) {
    return null;
  }

  const showDays = days > 0;
  const showHours = showDays || hours > 0;
  const showMinutes = showHours || minutes > 0;

  return (
    <Stack
      role="timer"
      data-testid="live-countdown"
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: { xs: "100%", sm: "auto" },
        gap: "1.2rem"
      }}
    >
      {showDays && (
        <>
          <TimeUnit value={days} label="Days" isLight={isLight} />
          <TimeSeparator unit="days" />
        </>
      )}
      {showHours && (
        <>
          <TimeUnit value={hours} label="Hours" isLight={isLight} />
          <TimeSeparator unit="hours" />
        </>
      )}
      {showMinutes && (
        <>
          <TimeUnit value={minutes} label="Minutes" isLight={isLight} />
          <TimeSeparator unit="minutes" />
        </>
      )}
      <TimeUnit value={seconds} label="Seconds" isLight={isLight} />
    </Stack>
  );
};

const LiveCountdown = ({ endTime, isLight }: { endTime: number; isLight: boolean }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return <Countdown date={endTime} renderer={(props) => renderTimer(props, isLight)} />;
};

export default LiveCountdown;